import { ref, watch } from "vue";
import { defineStore } from "pinia";
import { Monitor } from "@/utils/monitor";
import { Logger } from "@/logger";
import api from "@/APIClient";
import $ls, { LocalStorageKey } from "@/utils/local-storage";

export const MOUSE_EVENTS = ["mousedown", "mousemove", "mouseup", "mouseleave"];

export const useDeviceState = defineStore("deviceState", () => {
  const isOnline = ref<boolean | null>(null);
  const offlineSince = ref<Date | null>(
    $ls.getDateItem(LocalStorageKey.OFFLINE_SINCE)
  );
  const onlineStateMonitor = new Monitor(checkIfOnline, { intervalInMs: 3000 });

  // Watch for online status changes to update offlineSince
  watch(isOnline, (newValue, oldValue) => {
    if (newValue === false && oldValue !== false && !offlineSince.value) {
      const now = new Date();
      offlineSince.value = now;
      $ls.setDateItem(LocalStorageKey.OFFLINE_SINCE, now);
      $ls.removeItem(LocalStorageKey.OFFLINE_BANNER_DISMISSED);
    } else if (newValue === true && offlineSince.value !== null) {
      offlineSince.value = null;
      $ls.removeItem(LocalStorageKey.OFFLINE_SINCE);
      $ls.removeItem(LocalStorageKey.OFFLINE_BANNER_DISMISSED);
    }
  });

  // Refer to this article : https://hacks.mozilla.org/2013/04/detecting-touch-its-the-why-not-the-how/
  function isTouchDevice(): boolean {
    return (
      "ontouchstart" in window || // Most modern touch devices
      // (window.DocumentTouch && document instanceof DocumentTouch) || // Fallback for older devices //TODO outdated
      navigator.maxTouchPoints > 0 || // For some newer devices with maxTouchPoints property
      (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) // Detects coarse pointers, typical of touch screens
    );
  }

  async function checkIfOnline() {
    const apiOk = await checkApi();
    isOnline.value = apiOk;
    if (!apiOk) {
      Logger.info(`DeviceState.ts : isOnline ? api ${apiOk}`);
    }
  }

  async function checkApi(): Promise<boolean> {
    try {
      const data = await api.getHealth();
      return data.api.status == "ok" && data.database.status == "ok";
    } catch {
      Logger.error("DeviceState.ts : there was a problem checking api status");
      return false;
    }
  }

  function startMonitoringOnlineState() {
    onlineStateMonitor.start();
  }

  function stopMonitoringOnlineState() {
    onlineStateMonitor.stop();
  }

  // see https://getbootstrap.com/docs/5.3/layout/breakpoints/#available-breakpoints
  function isSmall() {
    return window.screen.width < 768;
  }

  return {
    isOnline,
    offlineSince,
    isTouchDevice,
    startMonitoringOnlineState,
    stopMonitoringOnlineState,
    isSmall,
    checkIfOnline,
  };
});
